import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';

import { LoadingContext, M_StateActions, MTabsInpage, NavigateContext } from 'src/M_Components';
import { hasPermission } from 'src/components/utils/permissionsHelper';

import CountryConfig from './countryConfig';
import ContinentConfig from './continentConfig';

// unified tabs styling (no behavior changes)
import 'src/pages/tabsInpage.css';

export default function CountryPage({ roles, onCreateTab, toastMessages, handleTabClose }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { renameTab } = useContext(NavigateContext);
    const { t, i18n } = useTranslation();
    const matches = useMediaQuery('(min-width:900px)');
    const M_State = useSelector((state) => state.StateKeyValue);
    const selectedTab = useSelector(s => s.StateKeyValue.find(o => o.key === 'selectedTab')?.value);
    const location = useLocation();

    const [menu, setMenu] = useState(1);
    const [refresh, setRefresh] = useState(false);

    // Dont fallback to a constant key; always use the current tab id
    const [id, setId] = useState(() => M_State.find(obj => obj.key === 'selectedTab')?.value);

    //============================================================================================================
    // Gets session storage data based on the session ID
    //============================================================================================================
    const getSessionStorageData = (sessionID) => {
        if (!sessionID) return;
        ctx.setIsLoad(true); // Set loading state to true
        const session = M_State.find(obj => obj.key === sessionID)?.value; // Find the session data by key
        if (session) { // If session data exists, retrieve the values
            const m = session.find(x => x.key === 'menu');
            const ref = session.find(x => x.key === 'refresh')?.value;
            if (m && typeof m.value === 'number') setMenu(m.value);
            if (ref !== undefined) setRefresh(ref);
        }
        ctx.setIsLoad(false); // Set loading state to false
    };

    //============================================================================================================
    // Rename tab when selectedTab or language changes
    //============================================================================================================
    useEffect(() => {
        if (!selectedTab) return; // Ensure selectedTab is defined
        renameTab(String(selectedTab), t('Countries')); // Rename the tab using the translation function
    }, [selectedTab, t]);

    //============================================================================================================
    // helper: upsert a single key under the per-tab namespace (merge, do not replace)
    //============================================================================================================
    const upsertSession = (tabId, kv) => {
        // kv: { key: string, value: any }
        const current = M_State.find(o => o.key === tabId)?.value || [];
        const filtered = current.filter(e => e.key !== kv.key);
        dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] }));
    };

    //============================================================================================================
    // EFFECT
    // Fetch session storage data when the component mounts or the location changes
    //============================================================================================================
    useEffect(() => {
        getSessionStorageData(id); // Call the function to get session data
        setRefresh(r => !r); // Toggle refresh state to trigger re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    //============================================================================================================
    // EFFECT
    // on selectedTab change, switch namespace and restore
    //============================================================================================================
    useEffect(() => {
        const newId = M_State.find(obj => obj.key === 'selectedTab')?.value;
        if (newId && newId !== id) {
            setId(newId);
            getSessionStorageData(newId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    //============================================================================================================
    // EFFECT
    // Persist only the 'menu' (and keep any other keys stored by children)
    //============================================================================================================
    useEffect(() => {
        if (!id) return;
        upsertSession(id, { key: 'menu', value: menu }); // <-- merge, not replace
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, menu /*, refresh*/]);

    //============================================================================================================
    // Define allowed tabs on the tabs in page
    //============================================================================================================
    const allowedTabs = () => {
        const tabArray = [
            { id: 1, labelTab: t('Countries'), iconTab: <i className="pi pi-globe" /> },
            { id: 2, labelTab: t('Continents'), iconTab: <i className="pi pi-compass" /> }
        ];
        return tabArray;
    };

    return (
        // Keep old classes for backward-compat while adopting the unified layout classes
        <div className="tabsInpage divGlobalSettings">
            <div className="tabsInpage__title titleSettings" style={{ color: ctx.secondaryColor }}>{t('Countries')}</div>
            {/* Use the standard split container so the new CSS controls layout/responsiveness */}
            <div className="tabsInpage__body">
                {/* Sidebar column (MTabsInpage). The CSS handles widths, borders and responsive behavior */}
                <div className="tabsInpage__sidebar">
                    <MTabsInpage // Tabs for navigating between countries and continents
                        type={matches ? 'column' : 'tabs'}
                        renderTabs={allowedTabs()}
                        firstTabOpen={menu}
                        onGetTab={(obj) => setMenu(obj.id)}
                        fixedArea={false}
                    />
                </div>
                {/* Right content area. Keep legacy class for bridge; no behavior changes */}
                <div className="tabsInpage__content settingsPageMainDiv">
                    {menu === 1 && (
                        <CountryConfig // Render component for countries options table
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                        />
                    )}
                    {menu === 2 && (
                        <ContinentConfig // Render component for continents options table
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
